import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLibraryAssets, useBrandAssetTags } from "@/hooks/use-library-assets";
import { useProjects } from "@/hooks/use-projects";
import { assetToMediaItem, type BrandAsset } from "@/hooks/use-brand-assets";
import { MediaPreview, MediaLightbox } from "@/components/media";

interface LibraryBrowserProps {
  brandId: string;
  /** Restrict to a single media type (picker mode); when null, a type filter UI is shown. */
  type?: string | null;
  selectable?: boolean;
  multi?: boolean;
  max?: number;
  selectedUrls?: string[];
  onSelectChange?: (urls: string[], assets: BrandAsset[]) => void;
  renderAssetFooter?: (asset: BrandAsset) => React.ReactNode;
}

const TYPE_OPTIONS = [
  { value: "", labelKey: "library.allTypes" },
  { value: "IMAGE", labelKey: "library.images" },
  { value: "VIDEO", labelKey: "library.videos" },
  { value: "DOCUMENT", labelKey: "library.documents" },
];

export function LibraryBrowser({
  brandId, type = null, selectable = false, multi = false, max,
  selectedUrls = [], onSelectChange, renderAssetFooter,
}: LibraryBrowserProps) {
  const { t } = useTranslation("common");
  const [rawQ, setRawQ] = useState("");
  const [q, setQ] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Debounce the search box before it reaches the query.
  useEffect(() => {
    const id = setTimeout(() => setQ(rawQ), 300);
    return () => clearTimeout(id);
  }, [rawQ]);

  const filters = useMemo(
    () => ({ type: type ?? (typeFilter || null), q, tags, projectId: projectId || null }),
    [type, typeFilter, q, tags, projectId],
  );
  const query = useLibraryAssets(brandId, filters);
  const tagList = useBrandAssetTags(brandId).data ?? [];
  const projects = useProjects(brandId).data ?? [];

  // De-dupe by id: a keyset-pagination boundary shift (concurrent insert) can
  // surface the same asset on two consecutive pages.
  const items: BrandAsset[] = useMemo(() => {
    const seen = new Set<string>();
    const out: BrandAsset[] = [];
    for (const p of query.data?.pages ?? []) {
      for (const a of (p as any).items as BrandAsset[]) {
        if (seen.has(a.id)) continue;
        seen.add(a.id);
        out.push(a);
      }
    }
    return out;
  }, [query.data]);

  // Accumulate every asset ever seen (across pages AND filter changes) so the
  // `assets` reported to selection consumers is complete even when a previously
  // selected asset is no longer in the currently-loaded/filtered `items`.
  const seenAssets = useRef<Map<string, BrandAsset>>(new Map());
  useEffect(() => {
    for (const a of items) seenAssets.current.set(a.url, a);
  }, [items]);

  const toggleTag = (t: string) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const toggleSelect = (asset: BrandAsset) => {
    if (!onSelectChange) return;
    seenAssets.current.set(asset.url, asset);
    const has = selectedUrls.includes(asset.url);
    const next = has
      ? selectedUrls.filter((u) => u !== asset.url)
      : !multi
        ? [asset.url]
        : max && selectedUrls.length >= max
          ? selectedUrls
          : [...selectedUrls, asset.url];
    // Resolve full assets from the accumulated map (not just loaded items).
    const nextAssets = next.map((u) => seenAssets.current.get(u)).filter(Boolean) as BrandAsset[];
    onSelectChange(next, nextAssets);
  };

  const lightboxItems = useMemo(() => items.map((a) => assetToMediaItem(a)), [items]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={rawQ}
          onChange={(e) => setRawQ(e.target.value)}
          placeholder={t("library.search")}
          aria-label={t("library.search")}
          className="h-8 flex-1 rounded border px-2 text-sm"
        />
        {!type && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by type"
            className="h-8 rounded border px-2 text-xs"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
          </select>
        )}
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Filter by project"
          className="h-8 rounded border px-2 text-xs"
        >
          <option value="">{t("library.allProjects")}</option>
          {(projects as any[]).map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
      </div>

      {tagList.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tagList.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={tags.includes(t)}
              onClick={() => toggleTag(t)}
              className={`rounded px-2 py-0.5 text-xs ${
                tags.includes(t) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t("library.noResults")}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((a, idx) => {
            const selected = selectedUrls.includes(a.url);
            return (
              <div key={a.id} className="group relative">
                {selectable ? (
                  <button
                    type="button"
                    aria-label={`Select ${a.filename}`}
                    aria-pressed={selected}
                    onClick={() => toggleSelect(a)}
                    className={`block w-full rounded-lg ${selected ? "ring-2 ring-primary" : ""}`}
                  >
                    <MediaPreview item={assetToMediaItem(a)} className="aspect-square w-full" />
                  </button>
                ) : (
                  <MediaPreview
                    item={assetToMediaItem(a)}
                    className="aspect-square w-full"
                    onOpen={() => setLightboxIndex(idx)}
                    label={a.filename}
                  />
                )}
                {selected && multi && (
                  <span className="absolute right-1 top-1 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                    {selectedUrls.indexOf(a.url) + 1}
                  </span>
                )}
                {!selectable && renderAssetFooter?.(a)}
              </div>
            );
          })}
        </div>
      )}

      {query.hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="rounded border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t("library.loading") : t("library.loadMore")}
          </button>
        </div>
      )}

      {!selectable && (
        <MediaLightbox
          items={lightboxItems}
          index={lightboxIndex}
          onIndexChange={(i) => setLightboxIndex(i)}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
